import React, { Component } from "react";
import { Button, Alert, ProgressBar } from "react-bootstrap";
import configData from "../../../config.json";

export default class ProcessingStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      processing: false,
      progress: {
        total_rows: props.session.totalRows || 0,
        processed_rows: props.session.processedRows || 0,
        success_count: props.session.successCount || 0,
        failed_count: props.session.failedCount || 0,
        percentage: 0,
      },
      processingMode: "immediate", // "immediate" or "background"
      estimatedTimeRemaining: null,
      startTime: null,
      currentStatus: props.session.status || "validated",
    };
    
    this.progressInterval = null;
  }

  componentDidMount() {
    // If already processing or completed, start monitoring
    if (this.state.currentStatus === "processing") {
      this.startProgressMonitoring();
    } else if (this.state.currentStatus === "completed" || this.state.currentStatus === "failed") {
      this.fetchFinalStatus();
    }
  }

  componentWillUnmount() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }

  startImport = () => {
    this.setState({ 
      processing: true, 
      startTime: Date.now(),
      currentStatus: "processing"
    });

    const formData = new FormData();
    formData.append("session_id", this.props.session.sessionId);
    formData.append("sId", sessionStorage.getItem("sessionId"));
    formData.append("uId", sessionStorage.getItem("uId"));
    
    // Pass import mode preference from validation step
    const importMode = this.props.session.importMode || 'valid_only';
    formData.append("import_mode", importMode);

    fetch(configData.api_url + "C_BulkImport/process", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          this.setState({ processingMode: json.processing_mode });
          
          if (json.processing_mode === "immediate") {
            // Processing completed immediately
            // Update the progress state with actual results
            this.setState({
              progress: {
                total_rows: json.result.total_processed || this.props.session.totalRows,
                processed_rows: json.result.total_processed || 0,
                success_count: json.result.successful || 0,
                failed_count: json.result.failed || 0,
                percentage: 100
              },
              currentStatus: "completed"
            }, () => {
              this.handleProcessingComplete(json.result);
            });
          } else {
            // Background processing - start monitoring
            this.startProgressMonitoring();
            this.props.onSuccess("Import started in background. Large files are processed in batches.");
          }
        } else {
          this.setState({ 
            processing: false,
            currentStatus: "failed"
          });
          this.props.onError(json.message || "Failed to start import");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ 
          processing: false,
          currentStatus: "failed"
        });
        this.props.onError("Network error occurred");
      });
  };

  startProgressMonitoring = () => {
    this.progressInterval = setInterval(() => {
      this.fetchProgress();
    }, 2000); // Check every 2 seconds
  };

  fetchProgress = () => {
    fetch(`${configData.api_url}C_BulkImport/status?session_id=${this.props.session.sessionId}`)
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          const progress = json.progress || {};
          const newStatus = json.import_status;
          
          this.setState({ 
            progress: {
              ...progress,
              percentage: progress.total_rows > 0 ? 
                Math.round((progress.processed_rows / progress.total_rows) * 100) : 0
            },
            currentStatus: newStatus
          });

          // Calculate estimated time remaining
          if (this.state.startTime && progress.processed_rows > 0) {
            const elapsed = Date.now() - this.state.startTime;
            const rate = progress.processed_rows / elapsed; // rows per ms
            const remaining = progress.total_rows - progress.processed_rows;
            const estimatedMs = remaining / rate;
            this.setState({ estimatedTimeRemaining: estimatedMs });
          }

          // Check if processing is complete
          if (newStatus === "completed" || newStatus === "failed") {
            this.handleProcessingComplete(json.summary);
          }
        }
      })
      .catch((error) => {
        console.error("Progress fetch error:", error);
      });
  };

  fetchFinalStatus = () => {
    fetch(`${configData.api_url}C_BulkImport/status?session_id=${this.props.session.sessionId}`)
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          const progress = json.progress || {};
          this.setState({ 
            progress: {
              ...progress,
              percentage: 100
            },
            processing: false,
            currentStatus: json.import_status
          });
          
          this.props.onUpdate({
            status: json.import_status,
            processedRows: progress.processed_rows,
            successCount: progress.success_count,
            failedCount: progress.failed_count,
            insertCount: json.summary?.insert_count || 0,
            updateCount: json.summary?.update_count || 0,
            skipCount: json.summary?.skip_count || 0,
          });
        }
      })
      .catch((error) => {
        console.error("Status fetch error:", error);
      });
  };

  handleProcessingComplete = (summary) => {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    this.setState({ processing: false });

    // Pass the result file path if available
    const updateData = {
      status: this.state.currentStatus,
      processedRows: this.state.progress.processed_rows,
      successCount: this.state.progress.success_count,
      failedCount: this.state.progress.failed_count,
      insertCount: summary?.insert_count || 0,
      updateCount: summary?.update_count || 0,
      skipCount: summary?.skip_count || 0,
    };
    
    if (summary?.result_file_path) {
      updateData.resultFilePath = summary.result_file_path;
    }

    this.props.onUpdate(updateData);

    if (this.state.currentStatus === "completed") {
      this.props.onSuccess("Import completed successfully!");
      this.props.onNext();
    } else {
      this.props.onError("Import failed. Please check the error logs.");
    }
  };

  cancelImport = () => {
    if (!window.confirm("Are you sure you want to cancel the import? Already processed records will remain in the database.")) {
      return;
    }

    const formData = new FormData();
    formData.append("session_id", this.props.session.sessionId);
    formData.append("sId", sessionStorage.getItem("sessionId"));
    formData.append("uId", sessionStorage.getItem("uId"));

    fetch(configData.api_url + "C_BulkImport/cancel", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "success") {
          this.setState({ 
            processing: false,
            currentStatus: "cancelled"
          });
          
          if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
          }
          
          this.props.onSuccess("Import cancelled successfully");
        } else {
          this.props.onError(json.message || "Failed to cancel import");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.props.onError("Network error occurred");
      });
  };

  formatTime = (milliseconds) => {
    if (!milliseconds) return "Calculating...";
    
    const seconds = Math.ceil(milliseconds / 1000);
    
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else if (seconds < 3600) {
      const minutes = Math.ceil(seconds / 60);
      return `${minutes} minutes`;
    } else {
      const hours = Math.ceil(seconds / 3600);
      return `${hours} hours`;
    }
  };

  renderProcessingProgress = () => {
    const { progress, estimatedTimeRemaining, currentStatus } = this.state;

    return (
      <div className="processing-container">
        <div className="progress-circle" style={{
          background: `conic-gradient(#007bff ${progress.percentage * 3.6}deg, #e9ecef ${progress.percentage * 3.6}deg)`
        }}>
          <div className="progress-inner">
            {progress.percentage}%
          </div>
        </div>

        <h5 className="mb-2">
          {currentStatus === "processing" ? "Processing Import..." : 
           currentStatus === "completed" ? "Import Completed!" :
           currentStatus === "failed" ? "Import Failed" :
           "Import Cancelled"}
        </h5>

        <div className="mb-4">
          <ProgressBar
            now={progress.percentage}
            label={`${progress.processed_rows} / ${progress.total_rows} rows`}
            variant={currentStatus === "failed" ? "danger" : 
                    currentStatus === "completed" ? "success" : "primary"}
          />
          {estimatedTimeRemaining && currentStatus === "processing" && (
            <div className="text-center mt-2 small text-muted">
              Estimated time remaining: {this.formatTime(estimatedTimeRemaining)}
            </div>
          )}
        </div>

        <div className="progress-stats">
          <div className="progress-stat">
            <div className="stat-number total">{progress.processed_rows}</div>
            <div className="stat-label">Processed</div>
          </div>
          <div className="progress-stat">
            <div className="stat-number success">{progress.success_count}</div>
            <div className="stat-label">Successful</div>
          </div>
          <div className="progress-stat">
            <div className="stat-number failed">{progress.failed_count}</div>
            <div className="stat-label">Failed</div>
          </div>
        </div>

        {currentStatus === "processing" && (
          <div className="mt-4">
            <Button
              variant="outline-danger"
              onClick={this.cancelImport}
              size="sm"
            >
              <i className="fas fa-stop me-2"></i>
              Cancel Import
            </Button>
          </div>
        )}
      </div>
    );
  };

  renderStartImport = () => {
    const { session } = this.props;
    const validRows = session.validationResults ? 
      session.validationResults.filter(row => row.is_valid).length : 
      session.totalRows;

    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i className="fas fa-rocket fa-4x text-primary mb-3"></i>
          <h4>Ready to Import Data</h4>
          <p className="text-muted">
            Your data has been validated and is ready for import into the database.
          </p>
        </div>

        <div className="row justify-content-center mb-4">
          <div className="col-md-8">
            <div className="d-flex justify-content-around text-center">
              <div>
                <div className="h5 text-primary mb-1">{session.totalRows}</div>
                <div className="small text-muted">Total Rows</div>
              </div>
              <div>
                <div className="h5 text-success mb-1">{validRows}</div>
                <div className="small text-muted">Valid Rows</div>
              </div>
              <div>
                <div className="h5 text-info mb-1">{session.targetTable}</div>
                <div className="small text-muted">Target Table</div>
              </div>
            </div>
          </div>
        </div>

        <Alert variant="info" className="mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-info-circle me-2"></i>
            <div className="text-start">
              <strong>What happens during import:</strong>
              <ul className="mb-0 mt-1">
                <li>Data is processed in chunks for optimal performance</li>
                <li>Duplicate records are handled based on your mapping settings</li>
                <li>Failed records are logged for later review and correction</li>
                <li>A detailed report will be generated upon completion</li>
              </ul>
            </div>
          </div>
        </Alert>

        <Button
          variant="primary"
          size="lg"
          onClick={this.startImport}
          disabled={this.state.processing}
          className="btn-primary-custom"
        >
          {this.state.processing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Starting Import...
            </>
          ) : (
            <>
              <i className="fas fa-play me-2"></i>
              Start Import
            </>
          )}
        </Button>
      </div>
    );
  };

  render() {
    const { currentStatus, processing } = this.state;

    if (currentStatus === "processing" || processing || 
        currentStatus === "completed" || currentStatus === "failed" || 
        currentStatus === "cancelled") {
      return this.renderProcessingProgress();
    }

    return this.renderStartImport();
  }
}